import { Platform, StyleSheet } from 'react-native';
import { Colors } from '../../../../themes/colors';
import { borderRadius, colors, spacing, typography } from '../../tokens';

/**
 * Ported style spec for the calendar component (plan 0017 §1). gripp-OS Tailwind
 * classes → Crop Circle tokens / `StyleSheet`. Where a gripp-OS color maps cleanly
 * to a production token it is used directly; the §6.4-flagged "gap" colors all turn
 * out to exist in the slate scale:
 *   surface-inverse-soft  #ECEEF1 → colors.slate[100]
 *   surface-table-header  #ECEEF1 → colors.slate[100]
 *   slate-200             #C8CDD5 → colors.slate[200]
 *   foreground-soft       #7A8394 → colors.slate[400]
 *   surface-inverse       #020202 → colors.neutral[800]
 *   subtle border         #F3F3F3 → colors.neutral[200]
 * The one genuine divergence: gripp-OS's brand orange is #FE5000 (Pantone 021C),
 * production's is #FF5505 (colors.brand.core). The drag-over ring (P3) uses the
 * production brand color rather than literally reproducing the gripp-OS hex.
 */

// White text on colored pills. gripp-OS pills use pure #FFFFFF (not brand.white
// #FCFCFC), so keep the full white for an exact match against the reference.
const PILL_WHITE = Colors.role.textOnBrand; // #FFFFFF in both modes
const PILL_WHITE_75 = 'rgba(255,255,255,0.75)'; // asset second line @ 75% (§1)
const PILL_SELECTED_RING = 'rgba(255,255,255,0.9)'; // inset selected ring (§1)
// Future/scheduled pill asset line: error-dark #B32A12 @ 70% (gripp-OS
// `text-semantic-error-dark/70`). RGB of #B32A12 = 179,42,18.
const FUTURE_ASSET_TEXT = 'rgba(179,42,18,0.7)';

// Web-only single-line truncation. RN's numberOfLines handles native; on web the
// CSS triad is what visually clips inside the fixed-width grid cells.
const truncate = Platform.select({
  web: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  default: {},
}) as object;

// tabular-nums keeps day numbers from shifting width digit-to-digit (§1). RN-Web
// passes fontVariant through to CSS font-variant-numeric.
const tabularNums = { fontVariant: ['tabular-nums' as const] };

export const calendarStyles = StyleSheet.create({
  // ── Event pill (§1 "Event pill") ──────────────────────────────────────────
  pill: {
    width: '100%',
    borderRadius: borderRadius.md, // 6
    paddingHorizontal: spacing.xs + 2, // 6 (no 6 token; xs=4 + 2)
    paddingVertical: 3, // no 3 token; literal per spec
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  pillDense: {
    paddingVertical: 1, // dense week variant (§1)
  },
  pillSelected: {
    // inset 2px ring via border; the pill keeps its color fill underneath.
    borderWidth: 2,
    borderColor: PILL_SELECTED_RING,
  },
  /**
   * Future / scheduled pill (gripp-OS `future` state): an alert that hasn't fired
   * yet reads as "upcoming" — a light-red surface with paired error-dark text
   * instead of the full type color. Once the activity's day is today or past it
   * drops this treatment and renders in full color (see EventPill `future` prop).
   */
  pillFuture: {
    backgroundColor: colors.semantic.errorLight, // #FFD9D2 (bg-semantic-error-light)
  },
  /** Future pill selected ring uses error-dark instead of white (gripp-OS). */
  pillSelectedFuture: {
    borderColor: colors.semantic.errorDark, // #B32A12
  },
  /** Origin pill while it is being dragged (§3): dim + lifted to read as "picked up". */
  pillDragging: {
    opacity: 0.5,
  },
  pillTitle: {
    ...typography.label.medium, // 12/16 — overridden below to 11/15 per spec
    fontSize: 11,
    lineHeight: 15,
    color: PILL_WHITE,
    width: '100%',
    ...truncate,
  },
  pillAsset: {
    fontSize: 10,
    lineHeight: 13,
    color: PILL_WHITE_75,
    width: '100%',
    ...truncate,
  },
  /** Future pill title: error-dark text on the light-red surface (gripp-OS). */
  pillTitleFuture: {
    color: colors.semantic.errorDark, // #B32A12 (text-semantic-error-dark)
  },
  /** Future pill asset line: error-dark @ 70% (gripp-OS `text-semantic-error-dark/70`). */
  pillAssetFuture: {
    color: FUTURE_ASSET_TEXT,
  },

  // ── Day number (§1 "Day number") ──────────────────────────────────────────
  dayNumber: {
    ...typography.label.medium, // 12/16
    color: Colors.role.textSoft, // foreground-soft
    ...tabularNums,
  },
  dayNumberOutOfMonth: {
    opacity: 0.5, // out-of-month dim (§1)
  },
  dayNumberTodayCircle: {
    width: 18,
    height: 18,
    borderRadius: borderRadius.full,
    backgroundColor: Colors.role.surfaceInverse, // surface-inverse
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNumberTodayText: {
    fontSize: 11,
    lineHeight: 11,
    fontWeight: '600',
    color: Colors.role.textOnInverse,
    ...tabularNums,
  },

  // ── Nav button (toolbar prev/next/today) ─────────────────────────────────
  navButton: {
    width: 28, // size-7 (28px) in gripp-OS toolbar
    height: 28,
    borderRadius: borderRadius.md, // 6 (rounded-md)
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButtonHovered: {
    backgroundColor: Colors.role.surfaceRaised, // hover:bg-secondary → soft slate
  },

  // ── Shared grid chrome (consumed by Month/Week grids in S2/S3) ────────────
  gridOuter: {
    borderWidth: 1,
    borderColor: Colors.role.borderSubtle, // subtle
    borderRadius: borderRadius.lg, // 8
    overflow: 'hidden',
  },
  /**
   * Fill the parent's height (flex: 1). Used by both grid roots so they fill the
   * column — MonthGrid so its 6 flex:1 rows grow tall enough for 2+ pills, and
   * WeekGrid so its inner ScrollView is bounded and actually scrolls (rather than
   * overflowing the column when an hour holds many events). Also applied to the
   * week ScrollView itself so it takes the height left under the fixed header.
   */
  gridOuterFill: {
    flex: 1,
  },
  /** A full row of 7 equal columns (DOW header / week of day cells). */
  gridRow: {
    flexDirection: 'row',
  },
  /** One of the 7 equal-width columns within a row. */
  gridCol: {
    flex: 1,
    minWidth: 0, // allow children to truncate instead of forcing column wider
  },
  headerRow: {
    backgroundColor: Colors.role.surfaceRaised, // surface-table-header
    borderBottomWidth: 1,
    borderColor: Colors.role.borderSubtle, // subtle
  },
  headerCellBg: {
    backgroundColor: Colors.role.surfaceRaised, // surface-table-header
  },
  headerCell: {
    paddingHorizontal: spacing.sm, // 8
    paddingVertical: spacing.sm, // 8
  },
  headerCellLabel: {
    fontSize: 11,
    lineHeight: 14,
    textTransform: 'uppercase',
    letterSpacing: 0.5, // tracking-wide
    color: Colors.role.textSoft, // foreground-soft
  },
  /** Month day cell: flex-col, gap 3, pad 6 (§1 "Cell"). */
  monthCell: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    gap: 3, // no 3 token; literal per spec
    padding: spacing.xs + 2, // 6 (xs=4 + 2)
    overflow: 'hidden',
  },
  cellBorder: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: Colors.role.borderSubtle, // subtle
  },
  /** Suppress the right border on the last column (§1 "no right border on col 7"). */
  cellNoRightBorder: {
    borderRightWidth: 0,
  },
  cellTodayBg: {
    backgroundColor: Colors.role.surfaceInverseSoftHover, // slate-200 #C8CDD5 (40% applied at use site)
  },
  /** drag-over inset ring (§1) — width only; color set inline from calendarColors. */
  cellDragOverRing: {
    borderWidth: 1,
    backgroundColor: Colors.role.surfaceSelected, // accent base under the ring
  },
  moreLabel: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.role.textSoft, // foreground-soft
    paddingHorizontal: spacing.xs, // 4
  },

  // ── Week grid (§1 "Week grid (hourly time-grid)") ─────────────────────────
  /** Week column header cell: 8/8 pad, baseline-aligned DOW + date (§1). */
  weekHeaderCell: {
    paddingHorizontal: spacing.sm, // 8
    paddingVertical: spacing.sm, // 8
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.xs, // 4
    backgroundColor: Colors.role.surfaceRaised, // surface-table-header (today overrides inline)
  },
  weekHeaderText: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.role.textSoft, // foreground-soft (today overrides inline)
  },
  /** Left border on columns after the first (§1 "Left border on cols after the first"). */
  cellLeftBorder: {
    borderLeftWidth: 1,
    borderColor: Colors.role.borderSubtle, // subtle
  },
  /** Hour slot: minHeight 40, pad 4, flex-col gap 4, bottom border (§1). */
  weekHourSlot: {
    flex: 1,
    minWidth: 0,
    minHeight: 40,
    padding: spacing.xs, // 4
    flexDirection: 'column',
    gap: spacing.xs, // 4
    borderBottomWidth: 1,
    borderColor: Colors.role.borderSubtle, // subtle
    overflow: 'hidden',
  },

  // ── Toolbar + mode toggle (§1 "Toolbar" / "Week/month mode toggle") ────────
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2, // 6 — gripp-OS gap-1.5
    paddingBottom: spacing.md, // 12 — gripp-OS pb-3
  },
  toolbarLabel: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: Colors.role.textStrong, // foreground-strong
  },
  toolbarSpacer: {
    flex: 1,
  },
  navGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xxs, // 2 — gripp-OS gap-0.5
  },
  /** Text "Today" button (28h, padH 10, 12px foreground-soft). */
  todayButton: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: borderRadius.md, // 6
    alignItems: 'center',
    justifyContent: 'center',
  },
  todayButtonHovered: {
    backgroundColor: Colors.role.surfaceRaised,
  },
  todayButtonText: {
    fontSize: 12,
    lineHeight: 16,
    color: Colors.role.textSoft, // foreground-soft
  },
  /** Mode-toggle pill container: bg surface-inverse-soft, radius 99, pad 2, gap 2, h 32 (§1). */
  modeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    padding: spacing.xxs, // 2
    gap: spacing.xxs, // 2
    borderRadius: borderRadius.pill, // 99
    backgroundColor: Colors.role.surfaceInverseSoft, // surface-inverse-soft
  },
  modeButton: {
    paddingHorizontal: spacing.md, // 12
    paddingVertical: spacing.xs, // 4
    borderRadius: borderRadius.pill, // 99
    alignItems: 'center',
    justifyContent: 'center',
  },
  modeButtonActive: {
    backgroundColor: Colors.role.surfaceInverse, // surface-inverse
  },
  modeButtonText: {
    fontSize: 12,
    lineHeight: 16,
    textTransform: 'capitalize',
    color: Colors.role.textStrong, // inactive
  },
  modeButtonTextActive: {
    color: Colors.role.textOnInverse,
  },

  // ── Calendar container layout ──────────────────────────────────────────────
  container: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  /** Grid region fills the remaining height; week's ScrollView needs this bound. */
  gridRegion: {
    flex: 1,
    minHeight: 0,
  },
  /** Loading spinner overlay — covers the grid while a window is fetching. */
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
} as const);

/**
 * Color/opacity constants the grids reference inline (where StyleSheet can't
 * express a runtime opacity blend or the drag-over ring color). Kept here so the
 * full §1 palette lives in one file.
 */
export const calendarColors = {
  /** Today-cell slate tint is applied at 40% (§1 "slate-200 @ 40%"). */
  todayCellTint: 'rgba(200,205,213,0.4)', // slate-200 #C8CDD5 @ 40%
  /** Out-of-month cell background at 40% (§1 "background @ 40%"). */
  outOfMonthCellTint: 'rgba(252,252,252,0.4)', // neutral-100 #FCFCFC @ 40%
  /**
   * Drag-over inset ring (§1 "brand-on-surface @ 40%"). Derived from the Gripp
   * brand-orange token (colors.brand.core) at 40% alpha via 8-digit hex (0x66 ≈
   * 0.4×255) so it tracks the token instead of a hardcoded rgba.
   */
  dragOverRing: `${colors.brand.core}66`, // brand.core (Gripp orange) @ 40%
  /** Today week-column header text (§1 "brand-on-surface"). */
  weekTodayHeaderText: colors.brand.core, // #FF5505
} as const;
